"use client";

import {
  useEffect,
  useRef,
  useState,
  useCallback,
  Fragment,
} from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import dynamic from "next/dynamic";
import StarField from "./StarField";
import HeroOverlay from "./HeroOverlay";
import { useIntersection } from "./_lib/useIntersection";
import { useIsMobile } from "./_lib/useMediaQuery";
import { usePrefersReducedMotion } from "./_lib/usePrefersReducedMotion";
import { useClientMounted } from "./_lib/useClientMounted";
import { setMotionLayers } from "./_lib/motionLayer";
import OptimizedPicture from "./_lib/OptimizedPicture";
import {
  PRIMARY_BUTTON_FILL,
  primaryButtonLabelClass,
  primaryButtonScaleLayerClass,
} from "./_lib/primaryButton";
import { BETA_ACCESS_FORM_URL } from "./_lib/betaAccessUrl";

const PhoneScene = dynamic(() => import("./PhoneScene"), { ssr: false });

const DEG2RAD = Math.PI / 180;

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.config({ ignoreMobileResize: true });

const NAV_LINKS = ["About", "AI", "Principles", "Customers", "Team"];
const MOBILE_NAV_MENU_ID = "mobile-nav-menu";
// px-4*2 + logo (~99px at h-5) + 40px gap + 24px hamburger + corner buffer
const MOBILE_NAV_PILL_WIDTH = 208;

const SCROLL_SECTIONS = [
  {
    badge: "For students",
    heading: ["Pearson courseware", "in your pocket"],
    description:
      "Every lesson maps directly to Focus courseware. Students practise what their teacher just taught.",
  },
  {
    badge: "For students",
    heading: ["Never forget", "your words again"],
    description:
      "Spaced repetition that brings words back at the right moment. Not cramming, building.",
  },
  {
    badge: "For students",
    heading: ["Learn together", "with friends"],
    description:
      "Points, badges, and avatars earned through practice. A profile worth coming back to.",
  },
  {
    badge: "For teachers",
    heading: ["Track learning", "without extra admin"],
    description:
      "Real engagement. No extra setup, no guessing who's practising.",
  },
];

const START_ROTATION = {
  x: -20 * DEG2RAD,
  y: 18 * DEG2RAD,
  z: 5 * DEG2RAD,
};

interface HeroSequenceProps {
  onReady?: () => void;
  loaderDone?: boolean;
}

export default function HeroSequence({ onReady, loaderDone }: HeroSequenceProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const heroOverlayRef = useRef<HTMLDivElement>(null);
  const heroTextRef = useRef<HTMLDivElement>(null);
  const descRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const waveRef = useRef<HTMLImageElement>(null);
  const starsRef = useRef<HTMLDivElement>(null);
  const vignetteRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const navShellRef = useRef<HTMLDivElement>(null);
  const navMenuRef = useRef<HTMLDivElement>(null);
  const navMenuItemsRef = useRef<(HTMLElement | null)[]>([]);
  const navAmbientRef = useRef<HTMLDivElement>(null);
  const navBorderGlowRef = useRef<HTMLDivElement>(null);
  const navScrimRef = useRef<HTMLDivElement>(null);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const hamburgerLine1Ref = useRef<HTMLSpanElement>(null);
  const hamburgerLine2Ref = useRef<HTMLSpanElement>(null);
  const hamburgerLine3Ref = useRef<HTMLSpanElement>(null);
  const navMenuTweenRef = useRef<gsap.core.Timeline | null>(null);
  const navPillWidthRef = useRef(0);
  const sectionsWrapperRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const gradientMaskRef = useRef<HTMLDivElement>(null);
  const scrollAnimInitRef = useRef(false);
  const readySentRef = useRef(false);

  const rotationRef = useRef({ ...START_ROTATION });
  const cursorRef = useRef({ x: 0, y: 0 });
  const screenIndexRef = useRef(0);

  const mounted = useClientMounted();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const isMobileLayout = useIsMobile();
  const prefersReducedMotion = usePrefersReducedMotion();
  const isStickyInView = useIntersection(stickyRef, {
    threshold: 0.05,
  }, true);

  useEffect(() => {
    ScrollTrigger.refresh();
  }, [isMobileLayout]);

  const showPhoneScene =
    mounted && !prefersReducedMotion && isStickyInView;
  const showStarField = mounted;

  useEffect(() => {
    const ids = NAV_LINKS.map((l) => l.toLowerCase());
    let rafId = 0;

    const getScrollOffset = () => window.innerHeight * 0.25;

    const updateActiveSection = () => {
      const sections = ids
        .map((id) => ({ id, el: document.getElementById(id) }))
        .filter((item): item is { id: string; el: HTMLElement } => item.el !== null);

      if (sections.length === 0) return;

      let nextActive = sections[0].id;
      for (const { id, el } of sections) {
        if (el.getBoundingClientRect().top <= getScrollOffset()) {
          nextActive = id;
        } else {
          break;
        }
      }

      setActiveSection(nextActive);
    };

    const scheduleUpdate = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        updateActiveSection();
      });
    };

    updateActiveSection();
    const retryTimer = window.setTimeout(updateActiveSection, 400);

    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      window.clearTimeout(retryTimer);
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuOpen((open) => !open);
  }, []);

  const blurMenuToggleIfIdle = useCallback(() => {
    if (menuOpen) return;
    const toggle = menuToggleRef.current;
    if (toggle && document.activeElement === toggle) {
      toggle.blur();
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!loaderDone || !mounted) return;
    blurMenuToggleIfIdle();
    const raf = requestAnimationFrame(blurMenuToggleIfIdle);
    const delayed = window.setTimeout(blurMenuToggleIfIdle, 300);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(delayed);
    };
  }, [loaderDone, mounted, blurMenuToggleIfIdle]);

  /* ---- Mobile menu a11y: Escape, focus trap, inert background ---- */
  useEffect(() => {
    if (!menuOpen || !isMobileLayout) return;

    const menu = navMenuRef.current;
    const toggle = menuToggleRef.current;
    if (!menu || !toggle) return;

    const inertTargets = [stickyRef.current, sectionsWrapperRef.current].filter(
      Boolean
    ) as HTMLElement[];
    inertTargets.forEach((el) => el.setAttribute("inert", ""));

    const getFocusables = () => {
      const nodes = menu.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      return [toggle, ...Array.from(nodes)].filter(
        (el) => !el.hasAttribute("disabled") && el.offsetParent !== null
      );
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMenu();
        return;
      }
      if (e.key !== "Tab") return;

      const focusables = getFocusables();
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        if (active === first || !menu.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      inertTargets.forEach((el) => el.removeAttribute("inert"));
    };
  }, [menuOpen, closeMenu, isMobileLayout]);

  const sendReady = useCallback(() => {
    if (readySentRef.current) return;
    readySentRef.current = true;
    onReady?.();
  }, [onReady]);

  useEffect(() => {
    if (!mounted) return;

    const connection = (
      navigator as Navigator & { connection?: { saveData?: boolean } }
    ).connection;
    const saveData = connection?.saveData;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced || saveData) {
      sendReady();
      return;
    }

    void document.fonts.ready.then(sendReady);
  }, [mounted, sendReady]);

  /* ---- All animations in one effect ---- */
  useEffect(() => {
    if (!loaderDone || !mounted || scrollAnimInitRef.current) return;

    const container = containerRef.current;
    const wrapper = canvasWrapperRef.current;
    const heroOverlay = heroOverlayRef.current;
    const desc = descRef.current;
    const glow = glowRef.current;
    const wave = waveRef.current;
    if (!container || !wrapper || !desc || !glow) return;

    const sections = sectionRefs.current.filter(Boolean) as HTMLDivElement[];
    const isMobile = isMobileLayout;

    /** Phone position after intro (description phase) */
    function getIntroTargetY() {
      return isMobile
        ? window.innerHeight * 0.13 - 55
        : window.innerHeight * 0.12 - 80;
    }

    /** Phone position during the 4 USP scroll sections: higher on mobile only */
    function getUspTargetY() {
      if (!isMobile) return getIntroTargetY();
      return window.innerHeight * 0.06 - 90;
    }

    function getSectionsTargetY() {
      return getIntroTargetY();
    }


    const mm = gsap.matchMedia();

    mm.add("(prefers-reduced-motion: reduce)", () => {
      const introTargetY = getIntroTargetY();
      gsap.set(wrapper, { opacity: 1, y: introTargetY });
      gsap.set(desc, { opacity: 0 });
      gsap.set(glow, { opacity: 1 });
      if (heroOverlay) {
        gsap.set(heroOverlay, { opacity: 0 });
        const reducedHeroGlow = heroOverlay.querySelector<HTMLElement>("[data-hero-glow]");
        if (reducedHeroGlow) gsap.set(reducedHeroGlow, { opacity: 0 });
      }
      if (vignetteRef.current) gsap.set(vignetteRef.current, { opacity: 0 });
      if (gradientMaskRef.current) gsap.set(gradientMaskRef.current, { opacity: 0 });
      if (starsRef.current) gsap.set(starsRef.current, { opacity: 0 });
      if (sectionsWrapperRef.current) {
        gsap.set(sectionsWrapperRef.current, { y: getSectionsTargetY() });
      }
      sections.forEach((section) => {
        gsap.set(section, { opacity: 1, y: 0 });
      });
      if (navRef.current) gsap.set(navRef.current, { opacity: 1, yPercent: 0 });
      menuToggleRef.current?.blur();

      const heroWords = heroOverlay?.querySelectorAll<HTMLElement>("[data-hero-word]");
      heroWords?.forEach((word) => {
        gsap.set(word, { opacity: 1, filter: "blur(0px)", y: 0 });
      });
      const heroSubtitle = heroOverlay?.querySelector<HTMLElement>("[data-hero-subtitle]");
      if (heroSubtitle) gsap.set(heroSubtitle, { opacity: 1, y: 0 });

      Object.assign(rotationRef.current, { x: 0, y: 0, z: 0 });
      screenIndexRef.current = 1;
      document.body.style.overflow = "";
      scrollAnimInitRef.current = true;

      return () => {
        scrollAnimInitRef.current = false;
      };
    });

    mm.add("(prefers-reduced-motion: no-preference)", () => {
    const sectionCount = sections.length;
    const SECTIONS_START = 0.48;
    const SECTIONS_END = 1.0;
    const sectionSpan = (SECTIONS_END - SECTIONS_START) / sectionCount;

    const restY = isMobile ? 115 : 200;

    /* -- Set initial states -- */
    gsap.set(wrapper, { opacity: 0, y: restY + (isMobile ? 200 : 400) });
    gsap.set(desc, { opacity: 0, y: isMobile ? 40 : 80 });
    const descWords = desc.querySelectorAll<HTMLElement>("[data-desc-word]");

    descWords.forEach((w) => gsap.set(w, { x: 50 }));
    gsap.set(glow, { opacity: 0 });
    const heroGlow = heroOverlay?.querySelector<HTMLElement>("[data-hero-glow]");
    if (heroGlow) gsap.set(heroGlow, { opacity: 0 });
    sections.forEach((s) => gsap.set(s, { opacity: 0, y: 60 }));

    const spinStart = {
      x: 50 * DEG2RAD,
      y: -15 * DEG2RAD,
      z: 5 * DEG2RAD,
    };
    Object.assign(rotationRef.current, spinStart);

    /* -- Build scroll timeline immediately (disabled until intro ends) -- */
    const tweens: gsap.core.Tween[] = [];
    const introTargetY = getIntroTargetY();
    const uspTargetY = getUspTargetY();
    const sectionsTargetY = getSectionsTargetY();
    const sectionFade = sectionSpan * 0.14;
    const descFadeIn = 0.22;
    const descFadeOut = 0.34 + descWords.length * 0.002 + 0.06;

    const hideDesc = () => {
      desc.style.opacity = "0";
      descWords.forEach((w) => {
        w.style.opacity = "0";
      });
    };

    const hideSections = () => {
      sections.forEach((section) => {
        section.style.opacity = "0";
        section.style.transform = "translate3d(0, 24px, 0)";
      });
    };

    const restoreHeroStart = () => {
      const ht = heroTextRef.current;
      if (ht) {
        ht.style.maskImage = "none";
        ht.style.webkitMaskImage = "none";
        ht.style.filter = "blur(0px)";
      }
      if (heroOverlay) {
        heroOverlay.style.opacity = "1";
      }
      const sw = starsRef.current;
      if (sw) {
        sw.style.opacity = "1";
        sw.style.transform = "translate3d(0, 0, 0)";
      }
    };

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: "top top",
        end: "bottom bottom",
        scrub: isMobile ? 0.45 : 0.35,
        onUpdate: (self) => {
          const p = self.progress;
          const dir = self.direction;
          if (p < SECTIONS_START) {
            screenIndexRef.current = 0;
          } else {
            const sectionLocal = (p - SECTIONS_START) / sectionSpan;
            screenIndexRef.current = Math.min(
              sectionCount,
              Math.max(1, Math.floor(sectionLocal) + 1)
            );
          }

          // Belt-and-braces resets so intro / USP copy never sticks after a
          // partial scroll down and reverse (common on mobile with scrub lag).
          if (p < 0.02) {
            restoreHeroStart();
            hideDesc();
            hideSections();
          } else {
            if (p < descFadeIn - 0.02 || p > descFadeOut) {
              hideDesc();
            }

            if (p < SECTIONS_START) {
              const enteringFirstSection =
                dir > 0 && p > SECTIONS_START - sectionFade;
              if (!enteringFirstSection) {
                hideSections();
              }
            }
          }
        },
      },
    });

    tl.scrollTrigger!.disable();

    /* --- INTRO PHASE (0% -- 48%) --- */

    if (heroOverlay) {
      // Explicit fromTo so the FROM (opacity: 1) is locked and reliably
      // restored on reverse scroll, even when ScrollTrigger refreshes after
      // the user scrolls past the end and back.
      tl.fromTo(
        heroOverlay,
        { opacity: 1 },
        { opacity: 0, ease: "power1.in", duration: 0.08 },
        0
      );
    }

    const heroText = heroTextRef.current;
    if (heroText) {
      const wipeObj = { progress: 0 };
      tl.fromTo(
        wipeObj,
        { progress: 0 },
        {
          progress: 1,
          ease: "power2.in",
          duration: 0.1,
          onUpdate: () => {
            const p = wipeObj.progress;
            // When fully reversed, drop the mask completely so it can never
            // end up stuck in a "transparent" state on the user's machine
            // (some Chrome builds intermittently retain the prior mask after
            // a fast reverse scroll).
            if (p <= 0.001) {
              heroText.style.maskImage = "none";
              heroText.style.webkitMaskImage = "none";
              heroText.style.filter = "blur(0px)";
              return;
            }
            const top = -30 + p * 130;
            const bottom = top + 30;
            const mask = `linear-gradient(to bottom, transparent ${top}%, black ${bottom}%)`;
            heroText.style.maskImage = mask;
            heroText.style.webkitMaskImage = mask;
            heroText.style.filter = "none";
          },
        },
        0
      );
    }

    const vignette = vignetteRef.current;
    if (vignette) {
      tl.to(
        vignette,
        { opacity: 0, ease: "power1.in", duration: 0.14 },
        0
      );
    }

    tl.fromTo(
      rotationRef.current,
      { x: START_ROTATION.x, y: START_ROTATION.y, z: START_ROTATION.z },
      {
        x: 0,
        y: 0,
        z: 0,
        ease: "power2.out",
        duration: 0.38,
        immediateRender: false,
      },
      0
    );

    tl.fromTo(
      wrapper,
      { y: restY },
      {
        y: introTargetY,
        ease: "power2.inOut",
        duration: 0.42,
        immediateRender: false,
      },
      0
    );

    const gradientMask = gradientMaskRef.current;
    if (gradientMask) {
      gsap.set(gradientMask, { opacity: 0 });
      tl.to(
        gradientMask,
        { opacity: 1, ease: "power1.in", duration: 0.07 },
        0.2
      );
      tl.to(
        gradientMask,
        { opacity: 0, ease: "power1.out", duration: 0.06 },
        0.36
      );
    }

    tl.to(
      desc,
      { opacity: 1, y: 0, ease: "power1.out", duration: 0.06 },
      0.22
    );
    descWords.forEach((w, i) => {
      tl.to(
        w,
        {
          opacity: 1,
          x: 0,
          ease: "power2.out",
          duration: 0.035,
        },
        0.22 + i * 0.002
      );
    });
    descWords.forEach((w, i) => {
      tl.to(
        w,
        {
          opacity: 0,
          x: -50,
          ease: "power2.in",
          duration: 0.035,
        },
        0.34 + i * 0.002
      );
    });
    tl.to(
      desc,
      { opacity: 0, ease: "power1.in", duration: 0.06 },
      0.34 + descWords.length * 0.002
    );

    const sectionsWrapper = sectionsWrapperRef.current;
    if (sectionsWrapper) {
      tl.to(
        sectionsWrapper,
        { y: sectionsTargetY, ease: "power2.inOut", duration: 0.1 },
        0.38
      );
    }

    /* --- SECTIONS PHASE (48% -- 100%) --- */

    if (isMobile) {
      const uspLiftDuration = sectionSpan * 0.92;
      tl.fromTo(
        wrapper,
        { y: introTargetY },
        { y: uspTargetY, ease: "none", duration: uspLiftDuration },
        SECTIONS_START
      );
    }

    sections.forEach((section, i) => {
      const start = SECTIONS_START + i * sectionSpan;
      const exitAt = start + sectionSpan - sectionFade;
      tl.fromTo(
        section,
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, ease: "power2.out", duration: sectionFade },
        start
      );
      if (i < sectionCount - 1) {
        tl.to(
          section,
          { opacity: 0, y: -24, ease: "power2.in", duration: sectionFade },
          exitAt
        );
      }
    });

    const lastSectionStart = SECTIONS_START + (sectionCount - 1) * sectionSpan;
    tl.to(
      glow,
      { opacity: 0, ease: "power1.in", duration: sectionSpan * 0.95 },
      lastSectionStart
    );

    /* --- PARALLAX (0% -- 100%) --- */

    if (wave) {
      tl.to(wave, { y: "-100%", ease: "none", duration: 1 }, 0);
    }

    const starsWrapper = starsRef.current;
    if (starsWrapper) {
      gsap.set(starsWrapper, { opacity: 1, y: 0 });
      // Explicit fromTo locks the FROM y/opacity in so the wrapper always
      // returns to (0, opacity 1) on reverse scroll. Avoids a stuck "off
      // screen + transparent" state after scrolling past the trigger.
      tl.fromTo(
        starsWrapper,
        { y: 0 },
        { y: "-100%", ease: "none", duration: SECTIONS_START },
        0
      );
      tl.fromTo(
        starsWrapper,
        { opacity: 1 },
        { opacity: 0, ease: "power1.in", duration: 0.08 },
        SECTIONS_START - 0.08
      );
    }

    const motionTargets = [
      wrapper,
      wave,
      starsWrapper,
      sectionsWrapperRef.current,
      glow,
    ].filter(Boolean) as HTMLElement[];

    tl.eventCallback("onStart", () => setMotionLayers(motionTargets, true));
    tl.eventCallback("onReverseComplete", () => {
      setMotionLayers(motionTargets, false);
      restoreHeroStart();
      hideDesc();
      hideSections();
    });

    /* -- Entrance intro (enables scroll timeline on complete) -- */

    window.scrollTo(0, 0);
    document.body.style.overflow = "hidden";

    const introTl = gsap.timeline({
      onComplete: () => {
        Object.assign(rotationRef.current, START_ROTATION);
        gsap.set(wrapper, { y: restY });
        document.body.style.overflow = "";
        tl.scrollTrigger!.enable();
        ScrollTrigger.refresh();
        gsap.set(glow, { opacity: 1 });
        if (heroGlow) gsap.set(heroGlow, { opacity: 0 });
        window.removeEventListener("wheel", skipIntro);
        window.removeEventListener("touchmove", skipIntro);
        window.removeEventListener("keydown", skipIntro);
        menuToggleRef.current?.blur();
      },
    });
    introTl.timeScale(isMobile ? 2.4 : 1);

    const skipIntro = () => {
      introTl.timeScale(introTl.timeScale() * 6);
    };
    window.addEventListener("wheel", skipIntro, { passive: true, once: true });
    window.addEventListener("touchmove", skipIntro, { passive: true, once: true });
    window.addEventListener("keydown", skipIntro, { once: true });

    const heroWords = heroOverlay?.querySelectorAll<HTMLElement>("[data-hero-word]");
    const heroSubtitle = heroOverlay?.querySelector<HTMLElement>("[data-hero-subtitle]");

    if (heroWords && heroWords.length > 0) {
      heroWords.forEach((word, i) => {
        introTl.to(
          word,
          {
            opacity: 1,
            filter: "blur(0px)",
            y: 0,
            duration: 0.8,
            ease: "power2.out",
          },
          0.1 + i * 0.12
        );
      });
    }

    if (heroSubtitle) {
      introTl.to(
        heroSubtitle,
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: "power2.out",
        },
        0.5
      );
    }

    introTl.to(
      wrapper,
      { opacity: 1, duration: 0.5, ease: "power2.out" },
      0.2
    );
    introTl.to(
      wrapper,
      { y: restY, duration: 1.6, ease: "power3.out" },
      0.2
    );
    introTl.to(
      rotationRef.current,
      {
        x: START_ROTATION.x,
        y: START_ROTATION.y,
        z: START_ROTATION.z,
        duration: 1.8,
        ease: "power3.out",
      },
      0.2
    );

    const glowFadeAt = 1.45;
    introTl.to(
      glow,
      { opacity: 1, duration: 0.75, ease: "power2.out" },
      glowFadeAt
    );
    if (heroGlow) {
      introTl.to(
        heroGlow,
        { opacity: 0, duration: 0.75, ease: "power2.out" },
        glowFadeAt
      );
    }

    if (navRef.current) {
      gsap.set(navRef.current, { yPercent: -50 });
      introTl.to(
        navRef.current,
        { opacity: 1, yPercent: 0, duration: 0.8, ease: "power3.out" },
        0.6
      );
    }

    tweens.push(introTl as unknown as gsap.core.Tween);

    let lastWidth = window.innerWidth;
    const onResize = () => {
      const width = window.innerWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      tl.invalidate();
      ScrollTrigger.refresh();
    };
    window.addEventListener("resize", onResize);
    scrollAnimInitRef.current = true;

    return () => {
      window.removeEventListener("wheel", skipIntro);
      window.removeEventListener("touchmove", skipIntro);
      window.removeEventListener("keydown", skipIntro);
      tweens.forEach((t) => t.kill());
      tl.scrollTrigger?.kill();
      tl.kill();
      setMotionLayers(motionTargets, false);
      window.removeEventListener("resize", onResize);
      scrollAnimInitRef.current = false;
    };
    });

    return () => {
      mm.revert();
      scrollAnimInitRef.current = false;
    };
  }, [loaderDone, mounted, prefersReducedMotion, isMobileLayout]);

  /* Fallback: reveal hero content if scroll animation never initialized */
  useEffect(() => {
    if (!loaderDone || !mounted || prefersReducedMotion) return;

    const timeout = window.setTimeout(() => {
      if (scrollAnimInitRef.current) return;

      const wrapper = canvasWrapperRef.current;
      const desc = descRef.current;
      const glow = glowRef.current;
      const heroOverlay = heroOverlayRef.current;
      const sections = sectionRefs.current.filter(Boolean) as HTMLDivElement[];

      if (wrapper) gsap.set(wrapper, { opacity: 1, y: 0 });
      if (desc) gsap.set(desc, { opacity: 0 });
      if (glow) gsap.set(glow, { opacity: 1 });
      if (heroOverlay) gsap.set(heroOverlay, { opacity: 0 });
      if (vignetteRef.current) gsap.set(vignetteRef.current, { opacity: 0 });
      if (gradientMaskRef.current) gsap.set(gradientMaskRef.current, { opacity: 0 });
      if (starsRef.current) gsap.set(starsRef.current, { opacity: 0 });
      if (sectionsWrapperRef.current) gsap.set(sectionsWrapperRef.current, { y: 0 });
      if (navRef.current) gsap.set(navRef.current, { opacity: 1, yPercent: 0 });

      sections.forEach((section) => {
        gsap.set(section, { opacity: 1, y: 0 });
      });

      menuToggleRef.current?.blur();
      document.body.style.overflow = "";
    }, 12000);

    return () => window.clearTimeout(timeout);
  }, [loaderDone, mounted, prefersReducedMotion]);

  /* ---- Cursor-reactive glow ---- */
  useEffect(() => {
    const sticky = stickyRef.current;
    if (!sticky) return;
    if (!window.matchMedia("(pointer: fine)").matches) return;

    const mouse = { x: 0, y: 0 };
    const smooth = { x: 0, y: 0 };
    let raf = 0;

    const STRENGTH = 30;
    const LERP = 0.07;
    const IDLE_EPS = 0.001;

    function handleMove(e: MouseEvent) {
      const rect = sticky!.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      mouse.y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      cursorRef.current.x = mouse.x;
      cursorRef.current.y = mouse.y;
      scheduleTick();
    }

    function handleLeave() {
      mouse.x = 0;
      mouse.y = 0;
      cursorRef.current.x = 0;
      cursorRef.current.y = 0;
      scheduleTick();
    }

    let motionOn = false;

    function tick() {
      raf = 0;

      if (!isStickyInView) {
        if (motionOn) {
          setMotionLayers([glowRef.current, sticky], false);
          motionOn = false;
        }
        return;
      }

      smooth.x += (mouse.x - smooth.x) * LERP;
      smooth.y += (mouse.y - smooth.y) * LERP;

      const tx = smooth.x * STRENGTH;
      const ty = smooth.y * STRENGTH;

      const idle =
        Math.abs(mouse.x - smooth.x) < IDLE_EPS &&
        Math.abs(mouse.y - smooth.y) < IDLE_EPS &&
        Math.abs(tx) < 0.05 &&
        Math.abs(ty) < 0.05;

      if (tx !== 0 || ty !== 0) {
        if (!motionOn) {
          setMotionLayers([glowRef.current, sticky], true);
          motionOn = true;
        }
      }

      if (glowRef.current) {
        glowRef.current.style.transform = `translate(${tx}px, ${ty}px)`;
      }

      sticky!.style.setProperty("--glow-tx", `${tx}px`);
      sticky!.style.setProperty("--glow-ty", `${ty}px`);

      if (!idle) {
        raf = requestAnimationFrame(tick);
      } else if (motionOn) {
        setMotionLayers([glowRef.current, sticky], false);
        motionOn = false;
      }
    }

    function scheduleTick() {
      if (raf) return;
      raf = requestAnimationFrame(tick);
    }

    sticky.addEventListener("mousemove", handleMove);
    sticky.addEventListener("mouseleave", handleLeave);

    return () => {
      sticky.removeEventListener("mousemove", handleMove);
      sticky.removeEventListener("mouseleave", handleLeave);
      cancelAnimationFrame(raf);
      setMotionLayers([glowRef.current, sticky], false);
    };
  }, [isStickyInView]);

  /* ---- Nav border glow tracking ---- */
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    if (!window.matchMedia("(pointer: fine)").matches) return;

    const onMove = (e: MouseEvent) => {
      const rect = nav.getBoundingClientRect();
      nav.style.setProperty("--mx", `${e.clientX - rect.left}px`);
      nav.style.setProperty("--my", `${e.clientY - rect.top}px`);
    };

    nav.addEventListener("mousemove", onMove);
    return () => nav.removeEventListener("mousemove", onMove);
  }, []);

  /* ---- Mobile nav morph (pill <-> panel) ---- */
  useEffect(() => {
    const nav = navRef.current;

    if (!isMobileLayout) {
      navMenuTweenRef.current?.kill();
      if (nav) gsap.set(nav, { clearProps: "width" });
      return;
    }

    const shell = navShellRef.current;
    const menu = navMenuRef.current;
    const ambient = navAmbientRef.current;
    const borderGlow = navBorderGlowRef.current;
    const scrim = navScrimRef.current;
    const line1 = hamburgerLine1Ref.current;
    const line2 = hamburgerLine2Ref.current;
    const line3 = hamburgerLine3Ref.current;
    const linkItems = navMenuItemsRef.current
      .slice(0, NAV_LINKS.length)
      .filter(Boolean) as HTMLElement[];
    const ctaItem = navMenuItemsRef.current[NAV_LINKS.length];
    const menuItems = [...linkItems, ...(ctaItem ? [ctaItem] : [])];
    if (!nav || !shell || !menu) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ease = reduced ? "power2.inOut" : "power3.inOut";
    const dur = reduced ? 0.28 : 0.52;
    const menuPadTop = 32;

    navMenuTweenRef.current?.kill();

    const measureMenuHeight = (atFullWidth: boolean) => {
      const prevNavWidth = nav.style.width;
      const prevHeight = menu.style.height;
      const prevPaddingTop = menu.style.paddingTop;
      const prevOverflow = menu.style.overflow;

      if (atFullWidth) {
        nav.style.width = "calc(100vw - 32px)";
      }
      menu.style.height = "auto";
      menu.style.paddingTop = `${menuPadTop}px`;
      menu.style.overflow = "hidden";
      gsap.set(menuItems, { y: 0, opacity: 1 });

      const maxH = window.innerHeight * 0.7;
      const contentH = menu.scrollHeight;
      const targetH = Math.min(contentH, maxH);
      const needsScroll = contentH > maxH;

      menu.style.height = prevHeight;
      menu.style.paddingTop = prevPaddingTop;
      menu.style.overflow = prevOverflow;
      nav.style.width = prevNavWidth;
      gsap.set(menuItems, { y: reduced ? 0 : 12, opacity: 0 });

      return { targetH, needsScroll };
    };

    const iconEase = reduced ? "power2.inOut" : "power4.inOut";

    const setHamburgerClosed = () => {
      if (!line1 || !line2 || !line3) return;
      gsap.set(line1, {
        y: 0,
        rotation: 0,
        opacity: 1,
        scaleX: 1,
        transformOrigin: "center",
        force3D: true,
      });
      gsap.set(line2, {
        y: 0,
        rotation: 0,
        opacity: 1,
        scaleX: 1,
        transformOrigin: "center",
        force3D: true,
      });
      gsap.set(line3, {
        y: 0,
        rotation: 0,
        opacity: 1,
        scaleX: 1,
        transformOrigin: "center",
        force3D: true,
      });
    };

    const animateHamburgerOpen = (
      tl: gsap.core.Timeline,
      at: number,
      iconDur: number
    ) => {
      if (!line1 || !line2 || !line3) return;
      tl.to(
        line1,
        { y: 7, rotation: 45, duration: iconDur, ease: iconEase },
        at
      );
      tl.to(
        line2,
        { opacity: 0, scaleX: 0, duration: iconDur * 0.42, ease: iconEase },
        at + iconDur * 0.1
      );
      tl.to(
        line3,
        { y: -7, rotation: -45, duration: iconDur, ease: iconEase },
        at
      );
    };

    const animateHamburgerClose = (
      tl: gsap.core.Timeline,
      at: number,
      iconDur: number
    ) => {
      if (!line1 || !line2 || !line3) return;
      tl.to(
        line1,
        { y: 0, rotation: 0, duration: iconDur, ease: iconEase },
        at
      );
      tl.to(
        line2,
        { opacity: 1, scaleX: 1, duration: iconDur * 0.42, ease: iconEase },
        at + iconDur * 0.12
      );
      tl.to(
        line3,
        { y: 0, rotation: 0, duration: iconDur, ease: iconEase },
        at
      );
    };

    if (menuOpen) {
      const pillW = shell.getBoundingClientRect().width;
      navPillWidthRef.current = pillW;
      const { targetH, needsScroll } = measureMenuHeight(true);

      gsap.set(nav, { width: pillW });
      gsap.set(menu, {
        height: 0,
        opacity: 0,
        paddingTop: 0,
        overflow: "hidden",
      });
      gsap.set(menuItems, { y: reduced ? 0 : 12, opacity: 0 });
      if (ambient) gsap.set(ambient, { opacity: 0 });
      if (borderGlow) gsap.set(borderGlow, { opacity: 0 });
      if (scrim) {
        gsap.set(scrim, { opacity: 0, pointerEvents: "none" });
      }
      setHamburgerClosed();

      const widthDur = dur;
      const iconDur = reduced ? widthDur * 0.75 : widthDur * 0.9;
      const heightDur = dur * 0.92;
      const openHeightAt = widthDur;
      const openItemsAt = openHeightAt + 0.06;

      navMenuTweenRef.current = gsap.timeline({
        defaults: { ease },
        onComplete: () => {
          if (needsScroll) {
            menu.style.overflowY = "auto";
          }
          navMenuItemsRef.current[0]?.focus();
        },
      });

      const openTl = navMenuTweenRef.current;

      openTl.to(nav, { width: "calc(100vw - 32px)", duration: widthDur }, 0);
      animateHamburgerOpen(openTl, 0, iconDur);

      if (scrim) {
        openTl.to(
          scrim,
          { opacity: 1, duration: widthDur * 0.9, pointerEvents: "auto" },
          0
        );
      }
      if (ambient) {
        openTl.to(ambient, { opacity: 1, duration: widthDur * 0.85 }, 0);
      }
      if (borderGlow) {
        openTl.to(borderGlow, { opacity: 0.65, duration: widthDur * 0.85 }, 0);
      }

      openTl.to(
        menu,
        {
          height: targetH,
          opacity: 1,
          paddingTop: menuPadTop,
          duration: heightDur,
        },
        openHeightAt
      );

      openTl.to(
        linkItems,
        {
          y: 0,
          opacity: 1,
          duration: reduced ? 0.18 : 0.34,
          stagger: reduced ? 0 : 0.04,
          ease: "power2.out",
        },
        openItemsAt
      );

      if (ctaItem) {
        openTl.to(
          ctaItem,
          {
            y: 0,
            opacity: 1,
            duration: reduced ? 0.18 : 0.34,
            ease: "power2.out",
          },
          openItemsAt
        );
      }
    } else {
      const pillW = navPillWidthRef.current || shell.getBoundingClientRect().width;

      navMenuTweenRef.current = gsap.timeline({
        defaults: { ease },
        onComplete: () => {
          gsap.set(nav, { clearProps: "width" });
          gsap.set(menu, {
            height: 0,
            opacity: 0,
            paddingTop: 0,
            overflow: "hidden",
            overflowY: "hidden",
          });
          gsap.set(menuItems, { clearProps: "transform,opacity" });
          if (ambient) gsap.set(ambient, { clearProps: "opacity" });
          if (borderGlow) gsap.set(borderGlow, { clearProps: "opacity" });
          if (scrim) {
            gsap.set(scrim, { opacity: 0, pointerEvents: "none" });
          }
          setHamburgerClosed();
          menuToggleRef.current?.focus();
        },
      });

      const closeHeightDur = dur * 0.78;
      const closeWidthDur = dur * 0.88;
      const closeIconDur = reduced ? closeWidthDur * 0.75 : closeWidthDur * 0.9;
      const closeHeightAt = 0.04;
      const closeWidthAt = closeHeightAt + closeHeightDur;

      const closeTl = navMenuTweenRef.current;

      closeTl.to(
        linkItems,
        {
          y: reduced ? 0 : 8,
          opacity: 0,
          duration: reduced ? 0.12 : 0.2,
          stagger: reduced ? 0 : 0.025,
          ease: "power2.in",
        },
        0
      );

      if (ctaItem) {
        closeTl.to(
          ctaItem,
          {
            y: reduced ? 0 : 8,
            opacity: 0,
            duration: reduced ? 0.12 : 0.2,
            ease: "power2.in",
          },
          0
        );
      }

      closeTl.to(
        menu,
        { height: 0, opacity: 0, paddingTop: 0, duration: closeHeightDur },
        closeHeightAt
      );

      if (scrim) {
        closeTl.to(
          scrim,
          { opacity: 0, duration: closeHeightDur * 0.85, pointerEvents: "none" },
          closeHeightAt
        );
      }
      if (ambient) {
        closeTl.to(ambient, { opacity: 0, duration: closeHeightDur * 0.85 }, closeHeightAt);
      }
      if (borderGlow) {
        closeTl.to(borderGlow, { opacity: 0, duration: closeHeightDur * 0.85 }, closeHeightAt);
      }

      closeTl.to(nav, { width: pillW, duration: closeWidthDur }, closeWidthAt);
      animateHamburgerClose(closeTl, closeWidthAt, closeIconDur);
    }

    return () => {
      navMenuTweenRef.current?.kill();
    };
  }, [menuOpen, isMobileLayout]);

  useEffect(() => {
    if (!mounted || !isMobileLayout) return;
    const menu = navMenuRef.current;
    const items = navMenuItemsRef.current.filter(Boolean) as HTMLElement[];
    if (!menu) return;
    gsap.set(menu, {
      height: 0,
      opacity: 0,
      paddingTop: 0,
      overflow: "hidden",
    });
    gsap.set(items, { opacity: 0 });
  }, [mounted, isMobileLayout]);

  useEffect(() => {
    const line1 = hamburgerLine1Ref.current;
    const line2 = hamburgerLine2Ref.current;
    const line3 = hamburgerLine3Ref.current;
    if (!line1 || !line2 || !line3) return;
    gsap.set([line1, line2, line3], {
      transformOrigin: "center",
      force3D: true,
    });
  }, [mounted, isMobileLayout]);

  return (
    <>
    <div
      ref={containerRef}
      id="about"
      className="relative"
      data-reduced-motion={prefersReducedMotion ? "true" : undefined}
      style={{
        height: prefersReducedMotion
          ? "auto"
          : isMobileLayout
            ? "700vh"
            : "1400vh",
      }}
    >
      <div
        ref={stickyRef}
        className="sticky top-0 min-h-[100dvh] h-[100dvh] w-full overflow-hidden bg-[#0D004D]"
      >
        {/* Fuchsia wave background */}
        <OptimizedPicture
          ref={waveRef}
          src="/assets/hero/wave-fuchsia.png"
          alt="Decorative fuchsia wave background"
          width={2048}
          height={1860}
          pictureClassName="contents"
          imgClassName="absolute bottom-0 left-1/2 w-[400vw] md:w-full max-w-none z-[0] pointer-events-none will-change-transform max-md:[transform:translate(-50%,85%)] md:[transform:translate(-50%,75%)]"
          draggable={false}
          fetchPriority="high"
        />

        {showStarField && (
          <div
            ref={starsRef}
            className="absolute inset-0 z-[1] pointer-events-none overflow-hidden"
          >
            <StarField />
          </div>
        )}

        <HeroOverlay ref={heroOverlayRef} textRef={heroTextRef} />

        {/* Vignette: soft top fade + corner darkening */}
        <div
          ref={vignetteRef}
          className="absolute inset-0 z-[14] pointer-events-none"
          style={{
            background: [
              "linear-gradient(to bottom, rgba(8, 0, 32, 0.10) 0%, rgba(8, 0, 32, 0.06) 16%, rgba(8, 0, 32, 0.025) 34%, transparent 58%)",
              "radial-gradient(ellipse 125% 85% at 50% 36%, transparent 18%, rgba(0, 0, 0, 0.10) 100%)",
              "linear-gradient(to right, rgba(0, 0, 0, 0.04) 0%, transparent 22%, transparent 78%, rgba(0, 0, 0, 0.04) 100%)",
            ].join(", "),
          }}
        />


        {/* 3D Phone with glow locked behind it */}
        <div className="absolute inset-0 flex items-center justify-center z-10 max-md:translate-y-[6%] md:-translate-y-[3%]">
          <div
            ref={canvasWrapperRef}
            className="relative"
            style={{
              height: isMobileLayout ? "76vh" : "85vh",
              aspectRatio: "2604 / 3180",
              maxWidth: "85vw",
              opacity: 0,
            }}
          >
            <div
              ref={glowRef}
              className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none"
              style={{ opacity: 0 }}
            >
              <svg
                viewBox="0 0 501 695"
                fill="none"
                className="w-[118%] h-[118%] absolute"
              >
                <g opacity="0.29" filter="url(#glow-filter)">
                  <ellipse
                    cx="250.4"
                    cy="347.4"
                    rx="142.5"
                    ry="239.5"
                    fill="url(#glow-gradient)"
                  />
                </g>
                <defs>
                  <filter
                    id="glow-filter"
                    x="0"
                    y="0"
                    width="500.8"
                    height="694.8"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="BackgroundImageFix"
                      result="shape"
                    />
                    <feGaussianBlur
                      stdDeviation="42"
                      result="effect1_foregroundBlur"
                    />
                  </filter>
                  <linearGradient
                    id="glow-gradient"
                    x1="107.9"
                    y1="107.9"
                    x2="528.871"
                    y2="358.373"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#A78BFA" />
                    <stop offset="1" stopColor="#D8B4FE" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <div className="relative z-[1] h-full w-full">
              {showPhoneScene && (
                <PhoneScene
                  rotationRef={rotationRef}
                  cursorRef={cursorRef}
                  screenIndexRef={screenIndexRef}
                />
              )}
            </div>
          </div>
        </div>

        {/* Gradient mask */}
        <div
          ref={gradientMaskRef}
          className="absolute inset-0 z-[10] pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, transparent 30%, rgba(13, 0, 77, 0.85) 65%, rgba(13, 0, 77, 1) 100%)",
            opacity: 0,
          }}
        />

        {/* Description text (intro copy) */}
        <div
          ref={descRef}
          className="absolute inset-0 z-[11] flex items-end pointer-events-none p-6 md:p-12 lg:p-20"
          style={{ opacity: 0 }}
        >
          <div className="max-w-[920px]">
            <p className="relative text-[clamp(24px,3.5vw,40px)] font-semibold leading-[1.056]">
              {"Language+ helps Pearson courseware students turn what they learn in class into real confidence in English, through short, daily practice outside the classroom: vocabulary, grammar, and speaking, on their own time.".split(" ").map((word, i) => (
                <span
                  key={i}
                  data-desc-word
                  className="inline-block mr-[0.25em] pb-[0.15em] text-white"
                  style={{ opacity: 0 }}
                >
                  {word}
                </span>
              ))}
            </p>
            <p className="relative mt-5 text-[clamp(24px,3.5vw,40px)] font-semibold leading-[1.056]">
              {"Always grounded in real pedagogy, designed to keep them engaged, on their favourite device.".split(" ").map((word, i) => (
                <span
                  key={i}
                  data-desc-word
                  className="inline-block mr-[0.25em] pb-[0.15em] text-white"
                  style={{ opacity: 0 }}
                >
                  {word}
                </span>
              ))}
            </p>
          </div>
        </div>

        {/* Scroll sections wrapper */}
        <div
          ref={sectionsWrapperRef}
          className="absolute inset-0 z-[11] pointer-events-none md:-translate-y-[3%]"
        >
          {SCROLL_SECTIONS.map((section, i) => (
            <div
              key={i}
              ref={(el) => {
                sectionRefs.current[i] = el;
              }}
              className="absolute inset-0 flex max-md:flex-col max-md:justify-end max-md:pb-[70px] max-md:px-6 md:items-center md:justify-center md:pb-0 md:px-8 lg:px-12 xl:px-24"
              style={{ opacity: 0 }}
            >
              {/* Mobile gradient: flush to viewport bottom */}
              <div
                className="absolute inset-x-0 bottom-0 z-0 md:hidden pointer-events-none h-[min(52vh,400px)]"
                style={{
                  background:
                    "linear-gradient(to top, rgba(13,0,77,0.95) 0%, rgba(13,0,77,0.85) 50%, rgba(13,0,77,0) 100%)",
                }}
              />
              {/* Text block */}
              <div className="relative z-[1] w-full max-w-[1200px] flex flex-col items-center gap-4 md:grid md:grid-cols-[1fr_300px_1fr] lg:grid-cols-[1fr_340px_1fr] xl:grid-cols-[1fr_420px_1fr] md:gap-5 lg:gap-7 xl:gap-16 md:items-center">
                {/* Left: badge + heading */}
                <div className="relative flex flex-col items-center text-center md:items-end md:text-right md:max-w-[280px] md:w-full md:ml-auto gap-3 max-md:gap-4">
                  <span className="inline-flex items-center px-4 py-1 rounded-full bg-white/10 text-white/80 text-sm font-medium tracking-wide">
                    {section.badge}
                  </span>
                  <h2
                    className="max-md:text-[clamp(32px,9vw,48px)] max-md:font-bold md:text-[clamp(22px,3.5vw,40px)] md:font-semibold leading-[1.1] tracking-tight pb-[0.12em] bg-clip-text text-transparent"
                    style={{
                      backgroundImage:
                        "linear-gradient(90deg, #ffffff 0%, #b7a8ea 100%)",
                    }}
                  >
                    {section.heading.map((line, j) => (
                      <Fragment key={j}>
                        {j > 0 && <br />}
                        {line}
                      </Fragment>
                    ))}
                  </h2>
                </div>

                {/* Center spacer */}
                <div className="hidden md:block md:w-[300px] lg:w-[340px] xl:w-[420px] h-[648px]" />

                {/* Right: description */}
                <div className="relative flex items-center justify-center md:justify-start md:max-w-[280px] md:w-full">
                  <p className="text-[#e8e8e8] text-[15px] md:text-lg leading-relaxed max-w-[300px] md:max-w-[260px] lg:max-w-[280px] xl:max-w-[320px] text-center md:text-left">
                    {section.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

      {/* Mobile menu scrim: rendered outside containerRef so it isn't trapped in its stacking context */}
      <div
        ref={navScrimRef}
        className="fixed inset-0 z-[9998] md:hidden bg-[#0D004D]/45 backdrop-blur-[3px] opacity-0 pointer-events-none"
        aria-hidden="true"
        onClick={closeMenu}
      />

      {/* Navigation: rendered as a sibling of containerRef so its z-index lives in the root stacking context and stays above pinned sections (CorePrinciples, etc.) */}
      <nav
        ref={navRef}
        className="fixed left-1/2 -translate-x-1/2 z-[9999] pointer-events-auto group/nav w-fit max-md:min-w-[208px] max-md:max-w-[calc(100vw-32px)] md:w-max"
        style={{
          top: "32px",
          opacity: 0,
          ...(isMobileLayout && !menuOpen
            ? { width: MOBILE_NAV_PILL_WIDTH, minWidth: MOBILE_NAV_PILL_WIDTH }
            : {}),
        }}
        aria-label="Main"
      >
        <div
          ref={navShellRef}
          className={`relative flex flex-col w-full rounded-[16px] bg-white/10 backdrop-blur-[20px] border border-white/20 px-4 pt-3 md:flex-row md:flex-nowrap md:items-center md:gap-12 md:px-6 md:py-4 md:w-max md:overflow-visible will-change-[width,height] ${
            menuOpen
              ? "overflow-hidden pb-4"
              : "max-md:overflow-visible overflow-hidden pb-3"
          }`}
        >
          <div
            ref={navAmbientRef}
            className="absolute inset-0 pointer-events-none rounded-[16px] opacity-0 md:hidden"
            style={{
              background:
                "radial-gradient(ellipse 120% 80% at 50% 0%, rgba(139,92,246,0.35) 0%, rgba(168,85,247,0.12) 45%, transparent 70%)",
            }}
          />

          <div
            className={`relative z-[1] flex shrink-0 items-center md:contents ${
              menuOpen ? "w-full justify-between" : "w-fit gap-[40px]"
            }`}
          >
          <img
            src="/assets/pearson-logo.svg"
            alt="Pearson"
            width={119}
            height={24}
            className="h-5 w-auto shrink-0 cursor-pointer brightness-0 invert md:h-6"
            onClick={() => {
              closeMenu();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
          <div className="hidden md:flex shrink-0 items-center gap-5 text-[14px] leading-[20px] text-white/80 whitespace-nowrap">
            {NAV_LINKS.map((link) => (
              <a
                key={link}
                href={`#${link.toLowerCase()}`}
                className={`transition-colors ${activeSection === link.toLowerCase() ? "text-white" : "hover:text-white"}`}
              >
                {link}
              </a>
            ))}
          </div>
          <a
            href={BETA_ACCESS_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`hidden md:inline-flex relative shrink-0 items-center rounded-full whitespace-nowrap transition-transform duration-300 hover:scale-[1.03] active:scale-[0.97] ${primaryButtonScaleLayerClass}`}
          >
            <span
              className={`relative z-[1] px-4 py-1.5 text-[13px] font-semibold ${primaryButtonLabelClass}`}
            >
              Get beta access
            </span>
            <span
              className="absolute inset-0 rounded-full"
              style={{ background: PRIMARY_BUTTON_FILL }}
            />
          </a>
          <button
            ref={menuToggleRef}
            type="button"
            className="relative h-6 w-6 shrink-0 md:hidden outline-none focus:outline-none focus-visible:outline-none [-webkit-tap-highlight-color:transparent]"
            onClick={toggleMenu}
            aria-expanded={menuOpen}
            aria-controls={MOBILE_NAV_MENU_ID}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            <span
              ref={hamburgerLine1Ref}
              className="absolute inset-x-0 top-[4px] block h-[2px] rounded-full bg-white will-change-transform"
            />
            <span
              ref={hamburgerLine2Ref}
              className="absolute inset-x-0 top-[11px] block h-[2px] rounded-full bg-white will-change-transform"
            />
            <span
              ref={hamburgerLine3Ref}
              className="absolute inset-x-0 bottom-[4px] block h-[2px] rounded-full bg-white will-change-transform"
            />
          </button>
          </div>

          <div
            id={MOBILE_NAV_MENU_ID}
            ref={navMenuRef}
            role="region"
            aria-label="Mobile navigation"
            className={`relative z-[1] flex flex-col overflow-hidden md:hidden ${
              menuOpen ? "w-full" : "w-0 min-w-0 max-w-0"
            }`}
            style={{ height: 0, opacity: 0, paddingTop: 0 }}
          >
            <div className="flex flex-col gap-4">
              {NAV_LINKS.map((link, i) => {
                const isActive = activeSection === link.toLowerCase();
                return (
                  <a
                    key={link}
                    ref={(el) => {
                      navMenuItemsRef.current[i] = el;
                    }}
                    href={`#${link.toLowerCase()}`}
                    onClick={closeMenu}
                    aria-current={isActive ? "page" : undefined}
                    className={`relative pl-4 text-[18px] leading-[26px] font-semibold transition-colors ${
                      isActive ? "text-white" : "text-white/75 hover:text-white"
                    }`}
                  >
                    {isActive && (
                      <span
                        className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full bg-violet-400"
                        aria-hidden="true"
                      />
                    )}
                    {link}
                  </a>
                );
              })}
            </div>
            <div className="pt-8">
              <a
                href={BETA_ACCESS_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                ref={(el) => {
                  navMenuItemsRef.current[NAV_LINKS.length] = el;
                }}
                onClick={closeMenu}
                className={`relative flex w-full items-center justify-center rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${primaryButtonScaleLayerClass}`}
              >
                <span
                  className={`relative z-[1] px-5 py-2.5 text-[15px] font-semibold ${primaryButtonLabelClass}`}
                >
                  Get beta access
                </span>
                <span
                  className="absolute inset-0 rounded-full border border-white/30"
                  style={{ background: PRIMARY_BUTTON_FILL }}
                />
              </a>
            </div>
          </div>
        </div>
        <div
          ref={navBorderGlowRef}
          className="absolute inset-0 rounded-[16px] pointer-events-none opacity-0 group-hover/nav:opacity-100"
          style={{
            padding: "1.5px",
            background:
              "radial-gradient(150px circle at var(--mx, 50%) var(--my, 50%), rgba(139,92,246,1), rgba(168,85,247,0.4) 40%, transparent 70%)",
            mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            maskComposite: "exclude",
            WebkitMaskComposite: "xor",
          }}
        />
        <div
          className="absolute -inset-3 rounded-[24px] pointer-events-none opacity-0 group-hover/nav:opacity-100 transition-opacity duration-700"
          style={{
            background:
              "radial-gradient(150px circle at var(--mx, 50%) var(--my, 50%), rgba(139,92,246,0.12), transparent 60%)",
            filter: "blur(8px)",
          }}
        />
      </nav>
    </>
  );
}
